import { type ShowWithMovie } from "@/lib/client/getShowsAndMovies";
import { type IPlannedDate, type IPlannedDates, type ISpillested } from "@/lib/kinologg/kinologgTypes";
import { parseISO } from "date-fns";

const _dateKey = Symbol("_showDate");

export type IFlatMappedPlannedDate = IPlannedDate & { spillested: ISpillested };
type WithDate<T> = T & { [_dateKey]: Date };

/**
 * The shows followed by the planned dates that don't already have a show at the same place on
 * the same day, sorted chronologically when combinePlannedDates is set. Shows are already sorted.
 * Shows without a start time are left out (ProgramShow renders nothing for them anyway).
 */
export function combineShowList(shows: ShowWithMovie[] | undefined, plannedDates: IPlannedDates[] | undefined, hidePlannedDates: boolean, combinePlannedDates: boolean): (ShowWithMovie | IFlatMappedPlannedDate)[] {
    let result: WithDate<ShowWithMovie | IFlatMappedPlannedDate>[] = [];
    if (shows) {
        result = [...prepareShows(shows)];
    }
    if (!hidePlannedDates && plannedDates) {
        const showDatesForLoc = result.map(s => {
            const show = s as ShowWithMovie;
            return { sted: show.location, dato: show.showStart?.substring(0, 10) }
        })
        const plannedShows = preparePlannedDates(plannedDates).filter(ps => !showDatesForLoc.some(val => val.sted === ps.spillested.stedsnavn && val.dato === ps.dato));

        result = [...result, ...plannedShows];
        if (combinePlannedDates) {
            // Now sort, so that all shows are in chronological order
            result.sort(sortShows);
        }
    }
    return result;
}

function preparePlannedDates(plannedDates: IPlannedDates[]): WithDate<IFlatMappedPlannedDate>[] {
    return plannedDates.flatMap(pd => pd.planned_dates?.map(p => ({ ...p, spillested: pd.spillested, [_dateKey]: parseISO(p.dato) })) || []);
}
function prepareShows(shows: ShowWithMovie[]): WithDate<ShowWithMovie>[] {
    return shows.flatMap(show => show.showStart ? [{ ...show, [_dateKey]: parseISO(show.showStart) }] : []);
}
function sortShows(a: WithDate<object>, b: WithDate<object>): number {
    return a[_dateKey].getTime() - b[_dateKey].getTime();
}
